import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { millProductionAPI } from '../../services/millAPI';
import './Mill.css';

interface Batch {
  id: number;
  batch_number: string;
  formula_name: string;
  feed_form: string;
  feed_form_display: string;
  planned_weight: number;
  actual_weight: number | null;
  status: string;
  status_display: string;
  planned_date: string | null;
  started_at: string | null;
  orders_count: number;
  total_allocated: number;
  ship_dates: string[];
}

const MillMixing: React.FC = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState<number | null>(null);
  const [showUpNext, setShowUpNext] = useState(false);

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await millProductionAPI.getBatches({ status: 'PLANNED,IN_PRODUCTION,IN_PROGRESS' });
      setBatches(response.data.results || []);
    } catch (err: any) {
      console.error('Error loading batches:', err);
      setError('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const handleStartBatch = async (batchId: number) => {
    try {
      setStarting(batchId);
      await millProductionAPI.startBatch(batchId);
      navigate(`/mill/mixing/${batchId}`);
    } catch (err: any) {
      console.error('Error starting batch:', err);
      alert('Failed to start batch: ' + (err.response?.data?.error || err.message));
      setStarting(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'PLANNED': return 'status-planned';
      case 'IN_PRODUCTION':
      case 'IN_PROGRESS': return 'status-in-progress';
      default: return '';
    }
  };

  const getFeedFormClass = (form: string) => {
    switch (form.toUpperCase()) {
      case 'MASH': return 'form-mash';
      case 'PELLET': return 'form-pellet';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="mill-page">
        <div className="mill-loading">
          <div className="mill-loading-spinner"></div>
          <p>Loading batches...</p>
        </div>
      </div>
    );
  }

  // Categorize batches
  const todayStr = new Date().toISOString().split('T')[0];

  // Get tomorrow as a work day (skip Sat/Sun/Mon)
  const getTomorrowWorkDay = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    while ([0, 1, 6].includes(d.getDay())) { // Sun=0, Mon=1, Sat=6
      d.setDate(d.getDate() + 1);
    }
    return d.toISOString().split('T')[0];
  };
  const tomorrowStr = getTomorrowWorkDay();

  const inProgressBatches = batches.filter(b => b.status === 'IN_PRODUCTION' || b.status === 'IN_PROGRESS');
  const overdueBatches = batches.filter(b => b.status === 'PLANNED' && b.planned_date && b.planned_date < todayStr);
  const todayBatches = batches.filter(b => b.status === 'PLANNED' && b.planned_date === todayStr);
  const tomorrowBatches = batches.filter(b => b.status === 'PLANNED' && b.planned_date === tomorrowStr);

  const todayTotalLbs = [...overdueBatches, ...todayBatches].reduce((s, b) => s + b.planned_weight, 0);
  const todayDate = new Date();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const todayLabel = `${dayNames[todayDate.getDay()]}, ${monthNames[todayDate.getMonth()]} ${todayDate.getDate()}`;

  const renderBatchCard = (batch: Batch, showStart: boolean = true) => (
    <div
      key={batch.id}
      className={`mill-batch-card ${getStatusClass(batch.status)}${!showStart ? ' preview' : ''}`}
      onClick={batch.status !== 'PLANNED' ? () => navigate(`/mill/mixing/${batch.id}`) : undefined}
      style={!showStart ? { opacity: 0.7 } : undefined}
    >
      <div className="mill-batch-header">
        <span className="mill-batch-number">{batch.batch_number}</span>
        <span className={`mill-status-badge ${getStatusClass(batch.status)}`}>
          {batch.status === 'PLANNED' ? 'Planned' : 'In Progress'}
        </span>
      </div>
      <div className="mill-batch-formula">{batch.formula_name}</div>
      <div className="mill-batch-meta">
        <span className={`mill-feed-form ${getFeedFormClass(batch.feed_form)}`}>
          {batch.feed_form_display}
        </span>
        <span className="mill-batch-weight">{batch.planned_weight.toLocaleString()} lbs</span>
      </div>
      <div className="mill-batch-info">
        <div className="info-item">
          <span className="info-label">Orders:</span>
          <span className="info-value">{batch.orders_count}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Ship:</span>
          <span className="info-value">
            {batch.ship_dates.length > 0 ? formatDate(batch.ship_dates[0]) : '-'}
          </span>
        </div>
      </div>
      {showStart && (
        <button
          className={`mill-btn ${batch.status === 'PLANNED' ? 'mill-btn-primary' : 'mill-btn-success'} mill-btn-block`}
          onClick={(e) => {
            e.stopPropagation();
            if (batch.status === 'PLANNED') {
              handleStartBatch(batch.id);
            } else {
              navigate(`/mill/mixing/${batch.id}`);
            }
          }}
          disabled={starting === batch.id}
        >
          {starting === batch.id ? 'Starting...' : batch.status === 'PLANNED' ? 'Start Mixing' : 'Continue Mixing'}
        </button>
      )}
    </div>
  );

  return (
    <div className="mill-page">
      <div className="mill-page-header">
        <h1>Today's Mixing Queue — {todayLabel}</h1>
        <p className="mill-page-subtitle">
          {overdueBatches.length + todayBatches.length + inProgressBatches.length} batch{overdueBatches.length + todayBatches.length + inProgressBatches.length !== 1 ? 'es' : ''} · {todayTotalLbs.toLocaleString()} lbs
        </p>
      </div>

      {error && <div className="mill-alert mill-alert-error">{error}</div>}

      {/* In Progress Section */}
      {inProgressBatches.length > 0 && (
        <div className="mill-section">
          <h2 className="mill-section-title">
            <span className="section-icon in-progress">●</span>
            In Progress ({inProgressBatches.length})
          </h2>
          <div className="mill-batch-grid">
            {inProgressBatches.map(batch => renderBatchCard(batch))}
          </div>
        </div>
      )}

      {/* Overdue Section */}
      {overdueBatches.length > 0 && (
        <div className="mill-section" style={{ borderLeft: '4px solid #dc3545', paddingLeft: 12 }}>
          <h2 className="mill-section-title" style={{ color: '#dc3545' }}>
            <span className="section-icon" style={{ color: '#dc3545' }}>●</span>
            OVERDUE ({overdueBatches.length})
          </h2>
          <div className="mill-batch-grid">
            {overdueBatches.map(batch => renderBatchCard(batch))}
          </div>
        </div>
      )}

      {/* Today Section */}
      <div className="mill-section">
        <h2 className="mill-section-title">
          <span className="section-icon planned">●</span>
          Today ({todayBatches.length})
        </h2>
        {todayBatches.length === 0 && overdueBatches.length === 0 && inProgressBatches.length === 0 ? (
          <div className="mill-empty-state">
            <p>No batches scheduled for today.</p>
            <button
              className="mill-btn mill-btn-primary"
              onClick={() => navigate('/mill/dashboard')}
            >
              Go to Dashboard
            </button>
          </div>
        ) : todayBatches.length === 0 ? (
          <div className="mill-empty-state">
            <p>No more batches scheduled for today.</p>
          </div>
        ) : (
          <div className="mill-batch-grid">
            {todayBatches.map(batch => renderBatchCard(batch))}
          </div>
        )}
      </div>

      {/* Up Next Section (collapsed by default) */}
      {tomorrowBatches.length > 0 && (
        <div className="mill-section" style={{ opacity: showUpNext ? 1 : 0.6 }}>
          <h2
            className="mill-section-title"
            style={{ cursor: 'pointer', color: '#888' }}
            onClick={() => setShowUpNext(!showUpNext)}
          >
            <span className="section-icon" style={{ color: '#888' }}>●</span>
            Up Next — {tomorrowBatches.length} batch{tomorrowBatches.length !== 1 ? 'es' : ''} tomorrow
            <span style={{ marginLeft: 8, fontSize: 14 }}>{showUpNext ? '▾' : '▸'}</span>
          </h2>
          {showUpNext && (
            <div className="mill-batch-grid">
              {tomorrowBatches.map(batch => renderBatchCard(batch, false))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MillMixing;
